// What is in the feeder, and what it is doing to the birds (PLAN-04 S5,
// REFOCUS §12a). Three things live here that used to be one line of drain:
//
// 1. **The ration is the player's choice.** Nothing switches a flock onto layer
//    feed when a chick turns into a pullet, and the two ways of getting it
//    wrong are not the same mistake. Layer feed on a growing bird is excess
//    calcium — NUT-04, above 2.0-2.5% Ca causes visceral gout and renal
//    failure — and it damages slowly and shows nothing. Grower or starter feed
//    on a layer is a calcium deficit — NUT-05/NUT-13 — and that one is quick
//    and visible: soft shells inside 48 hours, egg binding inside 72.
//
// 2. **Bags spoil.** Opened feed in tropical heat goes mouldy in 7-14 days
//    (NUT-19: over 80 degF and over 75% RH), and a sealed bin buys it time.
//
// 3. **Feed draws rats**, and rats cost feed and disease odds (design; ECO-06
//    is unresolved on every number in that sentence).
//
// Deterministic like the rest of src/sim: a bag's spoilage clock is drawn once,
// when it is opened, from a stream keyed to the bag.

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import type {
  ChickenEntity,
  FeedBag,
  FeedBagSize,
  FeedMode,
  FeedStage,
  GameState,
  Structure,
} from '../core/types';
import { newId } from '../core/initialState';
import { defaultFeedClass, feedClassOf, feedNeedScale } from './access';
import { rollStream } from './league';
import { getConfig, type BiologyConfig } from './config';
import { feedTypeForStage } from './lifecycle';
import { spotDef, spotForStructure } from './spots';
import { dayOf } from './time';

// --- feeding mode ------------------------------------------------------------

/** How the flock is fed. Free until the player says otherwise. */
export function feedingMode(state: GameState): FeedMode {
  return state.feeding?.mode ?? 'free';
}

/** Switches between a hanging feeder and a weighed daily ration. */
export function setFeedingMode(state: GameState, mode: FeedMode): void {
  state.feeding = { ...(state.feeding ?? { sealed: false }), mode };
}

/** True when open feed is in a sealed bin rather than a bucket. */
export function isFeedSealed(state: GameState): boolean {
  return state.feeding?.sealed ?? false;
}

/** Opens or seals the feed store. Does nothing to a bag that is already poured. */
export function setFeedSealed(state: GameState, sealed: boolean): void {
  state.feeding = { ...(state.feeding ?? { mode: 'free' as FeedMode }), sealed };
}

// --- bags --------------------------------------------------------------------

/** Which stage and size a bag item is, or null when it is not feed. */
export function bagSpecOf(
  itemId: string,
): { feedType: FeedStage; size: FeedBagSize } | null {
  const match = /^feed_(starter|grower|layer)_(small|big)$/.exec(itemId);
  if (match) return { feedType: match[1] as FeedStage, size: match[2] as FeedBagSize };
  // The original unsized bags are the small ones: the catalog's default.
  const plain = /^feed_(starter|grower|layer)$/.exec(itemId);
  return plain ? { feedType: plain[1] as FeedStage, size: 'small' } : null;
}

/** The item id a bag of this stage and size is bought as. */
export function feedItemId(feedType: FeedStage, size: FeedBagSize): string {
  return `feed_${feedType}_${size}`;
}

/**
 * Draws how long this bag will last once it is open, 7-14 days (NUT-19). Drawn
 * per bag rather than fixed, so two bags bought on the same day are not on the
 * same clock, and keyed to the bag so a replay lands the same way.
 */
function drawSpoilageDays(state: GameState, bagId: string, config: BiologyConfig): number {
  const rng = rollStream(state, `feedbag:${bagId}`);
  return rng.int(config.feed.spoilDaysMin, config.feed.spoilDaysMax);
}

/** Takes one bag out of the inventory and opens it. Null when there is none. */
export function openFeedBag(state: GameState, itemId: string): FeedBag | null {
  if ((state.inventory[itemId] ?? 0) <= 0) return null;
  const spec = bagSpecOf(itemId);
  if (!spec) return null;
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  const id = newId('bag', rollStream(state, `bagid:${itemId}:${state.inventory[itemId]}:${dayOf(state.simHour)}`));
  const bag: FeedBag = {
    id,
    itemId,
    feedType: spec.feedType,
    size: spec.size,
    openedAtHour: state.simHour,
    spoilageDays: drawSpoilageDays(state, id, config),
    mold: 0,
  };
  state.inventory[itemId] -= 1;
  (state.feedBags ??= []).push(bag);
  return bag;
}

/**
 * How fast this bag goes mouldy right now.
 *
 * Zero below either threshold — NUT-19 is explicit that it is heat AND
 * humidity together, and Puerto Rico crosses 80 degF most afternoons but sits
 * under 75% RH most nights, so a bag that spoiled on either alone would be
 * mouldy every morning of a San Juan summer. Sealed storage divides the rate by
 * the storage factor, which is the whole of what the bin buys.
 */
export function moldRate(
  bag: FeedBag,
  state: GameState,
  config: BiologyConfig = getConfig('normal'),
): number {
  const f = config.feed;
  if (state.weather.tempF <= f.spoilTempF || state.weather.humidity <= f.spoilHumidityPercent) {
    return 0;
  }
  const sealed = isFeedSealed(state) ? f.sealedShelfLifeFactor : 1;
  return 1 / (bag.spoilageDays * 24 * sealed);
}

/** The open bag behind a feeder, if there is one. */
export function bagForFeeder(state: GameState, feeder: Structure): FeedBag | undefined {
  const id = feeder.state.bagId;
  if (!id) return undefined;
  return state.feedBags?.find((bag) => bag.id === id);
}

/** The mouldiest open bag currently feeding a stocked feeder, 0-1. */
export function worstMold(state: GameState): number {
  let worst = 0;
  for (const feeder of state.structures) {
    if ((feeder.state.feed ?? 0) <= 0) continue;
    const bag = bagForFeeder(state, feeder);
    if (bag) worst = Math.max(worst, bag.mold);
  }
  return worst;
}

// --- the ration --------------------------------------------------------------

/**
 * What this bird should eat in a day, in the feeder's own units.
 *
 * The gauge has no grams behind it, so the ration is anchored to the rate the
 * game has always drained a feeder at for a heavy-breed layer, and the SHAPE
 * comes from the research: a Silkie eats 65 g where a Rhode Island Red eats
 * 122.5 (BRD-18 against BRD-17), and a chick eats 25. `feedNeedScale` is that
 * same ratio, so free and measured feeding cannot disagree about how much a
 * bantam wants.
 */
export function rationUnitsPerBirdPerDay(
  chicken: ChickenEntity,
  config: BiologyConfig = getConfig('normal'),
): number {
  return config.feed.drainUnitsPerBirdPerDay * feedNeedScale(chicken, config);
}

/** The whole flock's daily ration, in feeder units. */
export function flockRationUnitsPerDay(
  state: GameState,
  config: BiologyConfig = getConfig('normal'),
): number {
  return state.flock
    .filter((c) => c.alive)
    .reduce((sum, c) => sum + rationUnitsPerBirdPerDay(c, config), 0);
}

/**
 * What a feeder loses this step.
 *
 * Free feeding is a hanging feeder: some is eaten, some is on the ground, and
 * what is on the ground is what the rats find (design; ECO-06 is unresolved on
 * the waste and on everything downstream of it). Measured feeding is a weighed
 * ration, so there is no waste — and the whole flock is on one station, which
 * is what puts the bottom of the pecking order into S4's exclusion.
 */
export function feedDrainUnits(
  state: GameState,
  hours: number,
  autoFeed: boolean,
  config: BiologyConfig = getConfig('normal'),
): number {
  const waste = feedingMode(state) === 'free' ? 1 + config.feed.freeWasteFraction : 1;
  const auto = autoFeed ? 0.5 : 1;
  return (flockRationUnitsPerDay(state, config) / 24) * hours * waste * auto;
}

// --- rodents -----------------------------------------------------------------

function rodentState(state: GameState): NonNullable<GameState['rodents']> {
  return (state.rodents ??= { pressure: 0 });
}

/**
 * How much spilled feed this yard is offering the local rat population.
 *
 * Each feeder contributes its spot's `rodentExposure` — highest along the walls
 * and the run edge, which is where the mockup puts it — scaled by how much it is
 * actually spilling. A hanging feeder always sheds a little, because grain gets
 * flicked out of one all day; a trough that is nearly empty sheds a great deal,
 * because there is nothing holding the rest of it in. A weighed ration that is
 * full spills nothing at all, and one that is running dry spills exactly what a
 * hanging one would.
 *
 * The scale matters more than it looks. An earlier version returned the bare
 * exposure, so a yard on a hanging feeder was permanently at maximum rat
 * pressure no matter how well it was kept, and the pressure had no state to be
 * in. The multiplier and the whole decay are design values: ECO-06 found no
 * figure for a rat's intake, a population build rate, or a way to turn any of it
 * into a number.
 */
export function spilledFeedPressure(
  state: GameState,
  config: BiologyConfig = getConfig('normal'),
): number {
  let exposure = 0;
  for (const feeder of state.structures) {
    const level = feeder.state.feed ?? 0;
    if (level <= 0) continue;
    const spot = spotForStructure(state, feeder);
    const rodentExposure = spot?.rodentExposure ?? 0.5;
    const runningDry = level < 20 ? (20 - level) / 20 : 0;
    const spill =
      feedingMode(state) === 'free' ? config.feed.freeSpillFraction + runningDry : runningDry;
    exposure += rodentExposure * spill;
  }
  return exposure;
}

/** Rodent pressure, 0-1. */
export function rodentPressure(state: GameState): number {
  return rodentState(state).pressure;
}

/** The feed and the flock pressure rats cost, for this step. */
export function stepRodents(
  state: GameState,
  hours: number,
  config: BiologyConfig = getConfig('normal'),
): void {
  const f = config.feed;
  const rodents = rodentState(state);
  const gain = f.rodentGainPerHour * spilledFeedPressure(state, config) * hours;
  // Proportional, not flat. A flat hourly decay stops the pressure climbing
  // once the gain and the decay cancel, which pinned every yard that ever
  // spilled feed at 1.0 for ever; what a population does when the food stops
  // is die back, and a share of itself per hour is what that looks like. The
  // settled pressure is the gain over the decay, so the tuning is one number
  // and not two.
  rodents.pressure = clamp(
    rodents.pressure + (gain - f.rodentDecayPerHour * rodents.pressure * hours),
    0,
    1,
  );
}

/** Units a rat population eats out of the feeders this step. */
export function rodentFeedLoss(
  state: GameState,
  hours: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  return config.feed.rodentFeedLossPerHour * rodentPressure(state) * hours;
}

/** What the rats do to the birds, on top of the feed they eat. */
export function applyRodentPressure(
  state: GameState,
  hours: number,
  config: BiologyConfig = getConfig('normal'),
): void {
  const pressure = rodentPressure(state);
  if (pressure <= 0) return;
  for (const chicken of state.flock) {
    if (!chicken.alive) continue;
    chicken.physiology.stress = clamp(
      chicken.physiology.stress + config.feed.rodentStressPerHour * pressure * hours,
      0,
      100,
    );
  }
}

// --- feed stages -------------------------------------------------------------

/** Which way this bird's loaded ration is wrong for her, if it is. */
export type FeedStageProblem = 'excess_calcium' | 'deficit_calcium' | null;

/**
 * The stage error a bird is running, from the ration in her feeder and nothing
 * else. Layer ration is calcium-rich (it has to be, for the shell); starter and
 * grower ration is not. A chick on layer feed is being poisoned slowly; a layer
 * on chick feed is running out of the one thing she cannot do without.
 */
export function feedStageProblem(
  chicken: ChickenEntity,
  loaded: FeedStage | undefined,
): FeedStageProblem {
  if (!loaded) return null;
  const wanted = feedTypeForStage(chicken.stage);
  if (loaded === wanted) return null;
  // Layer ration is the only one carrying enough calcium to do harm in either
  // direction, so the two errors are the two ends of the same axis.
  return loaded === 'layer' ? 'excess_calcium' : 'deficit_calcium';
}

/**
 * One step of the calcium story.
 *
 * `hours` on the wrong ration does not do the same thing at 6 hours as at 96,
 * so both errors have an onset and only count after it: NUT-13's soft shells at
 * 48h and binding at 72h are the sourced spine, and NUT-04 gives the excess
 * direction a disease and no clock at all, so its onset is a design value.
 *
 * Returns the conditions the step started, for the report.
 */
export function stepFeedStages(
  state: GameState,
  hours: number,
  config: BiologyConfig = getConfig('normal'),
  rng: Rng,
): Array<{ chickenId: string; condition: 'egg_bound' }> {
  const f = config.feed;
  const started: Array<{ chickenId: string; condition: 'egg_bound' }> = [];
  const loaded = loadedFeedStage(state);

  for (const chicken of state.flock) {
    if (!chicken.alive) continue;
    const clock = (chicken.nutrition ??= { excessCalciumHours: 0, deficitCalciumHours: 0 });
    const problem = feedStageProblem(chicken, loaded);

    if (problem === 'excess_calcium') {
      clock.deficitCalciumHours = 0;
      // Only the part of the clock that is past the onset costs anything, and
      // only the part of THAT this step added. Charging the whole accumulated
      // overrun every hour bills a bird for damage she was already billed for.
      const before = clock.excessCalciumHours;
      clock.excessCalciumHours += hours;
      const over = Math.max(0, clock.excessCalciumHours - f.excessCaOnsetHours) -
        Math.max(0, before - f.excessCaOnsetHours);
      const p = chicken.physiology;
      p.bodyCondition = clamp(p.bodyCondition - f.excessCaDamagePerHour * over, 0, 100);
      p.immune = clamp(p.immune - f.excessCaImmunePerHour * over, 0, 100);
      continue;
    }

    if (problem === 'deficit_calcium') {
      clock.excessCalciumHours = 0;
      clock.deficitCalciumHours += hours;
      if (clock.deficitCalciumHours >= f.deficitCaShellHours) chicken.softShells = true;
      if (
        clock.deficitCalciumHours >= f.deficitCaBindingHours &&
        rng.chance(f.deficitCaBindingChance * hours)
      ) {
        // NUT-13's own condition, so the existing card, the existing recovery
        // clock and the existing lay stop all apply without a second model.
        const already = chicken.conditions.some((c) => c.id === 'egg_bound');
        if (!already) {
          chicken.conditions.push({
            id: 'egg_bound',
            severity: 'severe',
            startedAtHour: state.simHour,
            acknowledged: false,
          });
          started.push({ chickenId: chicken.id, condition: 'egg_bound' });
        }
      }
      continue;
    }

    // Correct ration: both clocks run down rather than snapping to zero, the
    // way a bird recovers from a short spell of the wrong bag.
    clock.excessCalciumHours = Math.max(0, clock.excessCalciumHours - hours);
    clock.deficitCalciumHours = Math.max(0, clock.deficitCalciumHours - hours);
    if (clock.deficitCalciumHours <= 0) chicken.softShells = false;
  }
  return started;
}

/** The stage most of the flock's stocked feeders are carrying, if any. */
export function loadedFeedStage(state: GameState): FeedStage | undefined {
  const stocked = state.structures.filter(
    (s) => (s.state.feed ?? 0) > 5 && s.state.feedType,
  );
  if (stocked.length === 0) return undefined;
  const tally = new Map<FeedStage, number>();
  for (const feeder of stocked) {
    const stage = feeder.state.feedType as FeedStage;
    tally.set(stage, (tally.get(stage) ?? 0) + 1);
  }
  return [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

/** The stage this flock needs now, from the birds in it. */
export function wantedFeedStage(state: GameState): FeedStage | undefined {
  const birds = state.flock.filter((c) => c.alive);
  if (birds.length === 0) return undefined;
  return feedTypeForStage(birds[birds.length - 1].stage);
}

/**
 * What a keeper needs to be told, on a stage change, in Normal mode.
 *
 * Veteran gets nothing: the mode's whole contract is that the sim does not
 * explain itself. The list is a report field, not a notification — S12 wires it
 * to a chip.
 */
export interface FeedStageReminder {
  chickenId: string;
  needs: FeedStage;
  has: FeedStage;
  problem: Exclude<FeedStageProblem, null>;
}

export function feedStageReminders(state: GameState, changed: string[]): FeedStageReminder[] {
  const loaded = loadedFeedStage(state);
  if (!loaded) return [];
  const out: FeedStageReminder[] = [];
  for (const chicken of state.flock) {
    if (!chicken.alive || !changed.includes(chicken.id)) continue;
    const problem = feedStageProblem(chicken, loaded);
    if (!problem) continue;
    out.push({
      chickenId: chicken.id,
      needs: feedTypeForStage(chicken.stage),
      has: loaded,
      problem,
    });
  }
  return out;
}

// --- the whole step ----------------------------------------------------------

/**
 * Everything the feed does in one sim-step: the bags age, the rats eat, the
 * mould gets into the birds, and the calcium clocks run. Called from
 * `simulate.ts` next to the other world systems, after the per-bird loop.
 */
export function stepFeed(
  state: GameState,
  hours: number,
  config: BiologyConfig,
  rng: Rng,
): Array<{ chickenId: string; condition: 'egg_bound' }> {
  // A bag that is not in any feeder is not being eaten out of, and does not
  // spoil: pouring it is what exposes it.
  for (const feeder of state.structures) {
    const bag = bagForFeeder(state, feeder);
    if (!bag) continue;
    bag.mold = clamp(bag.mold + moldRate(bag, state, config) * hours, 0, 1);
  }

  stepRodents(state, hours, config);
  applyRodentPressure(state, hours, config);

  // Mould is eaten, not smelled. The damage scales with the bag in the trough.
  const mold = worstMold(state);
  if (mold > 0) {
    for (const chicken of state.flock) {
      if (!chicken.alive) continue;
      chicken.physiology.gutHealth = clamp(
        chicken.physiology.gutHealth - config.feed.moldGutDamagePerHour * mold * hours,
        0,
        100,
      );
    }
  }

  return stepFeedStages(state, hours, config, rng);
}

/** The rodent exposure a spot offers, for the Setup screen's own layer. */
export function rodentExposureOf(state: GameState, spotId: string): number {
  return spotDef(state.housing, spotId)?.rodentExposure ?? 0.5;
}

/** Which feed class a breed's ration is read from, re-exported for the UI. */
export { defaultFeedClass, feedClassOf };
